import type {
  AiCreditsDto,
  GrantAiCreditsRequest,
  BranchDto,
  CreateTenantRequest,
  CreateTenantUserRequest,
  DeviceDetail,
  DeviceDto,
  DeviceWithToken,
  Paged,
  ResetPasswordRequest,
  TenantDetail,
  TenantSummary,
  TenantUserDto,
  CopySaSettingsRequest,
  CopySaSettingsResponse,
  DeviceSaSettings,
  UpdateSaSettingsRequest,
} from '@order-pilot/shared';
import { api, queryString } from '@/lib/api';

export interface TenantListParams {
  page: number;
  pageSize: number;
  search: string;
}

/** Fields the SA portal may PATCH on a tenant (derived from the shared DTO). */
export type UpdateTenantInput = Partial<
  Pick<
    TenantDetail,
    'name' | 'phone' | 'address' | 'timezone' | 'currency' | 'status' | 'smartMenuEnabled'
  >
> & {
  /** null clears the plan; undefined leaves it unchanged. */
  planId?: string | null;
};

export interface CreateBranchInput {
  name: string;
  address: string;
}

export interface CreateDeviceInput {
  branchId: string;
  label: string;
}

export const tenantKeys = {
  /** Prefix for every tenant query — invalidating this refreshes lists and details. */
  all: ['tenants'] as const,
  list: (params: TenantListParams) => ['tenants', 'list', params] as const,
  detail: (id: string) => ['tenants', 'detail', id] as const,
  /** One till with its licence. Under the tenants prefix so any tenant
      invalidation refreshes an open device page too. */
  device: (deviceId: string) => ['tenants', 'device', deviceId] as const,
  /** One till's SA settings. Under the tenants prefix like `device`. */
  saSettings: (deviceId: string) => ['tenants', 'sa-settings', deviceId] as const,
  aiCredits: (tenantId: string) => ['tenants', 'ai-credits', tenantId] as const,
};

export function fetchTenants(params: TenantListParams, signal?: AbortSignal): Promise<Paged<TenantSummary>> {
  // The API names the name filter `q`; it rejects unknown query params
  // (forbidNonWhitelisted), so sending `search` would 400 rather than degrade.
  const query = queryString({ page: params.page, pageSize: params.pageSize, q: params.search });
  return api.get<Paged<TenantSummary>>(`/admin/tenants${query}`, { signal });
}

export function fetchTenant(id: string, signal?: AbortSignal): Promise<TenantDetail> {
  return api.get<TenantDetail>(`/admin/tenants/${encodeURIComponent(id)}`, { signal });
}

export function createTenant(input: CreateTenantRequest): Promise<TenantDetail> {
  return api.post<TenantDetail>('/admin/tenants', input);
}

export function updateTenant(id: string, input: UpdateTenantInput): Promise<void> {
  return api.patch<void>(`/admin/tenants/${encodeURIComponent(id)}`, input);
}

export function uploadTenantLogo(id: string, file: File): Promise<TenantDetail> {
  const formData = new FormData();
  formData.append('logo', file);
  return api.uploadFile<TenantDetail>(`/admin/tenants/${encodeURIComponent(id)}/logo`, 'PUT', formData);
}

export function deleteTenantLogo(id: string): Promise<TenantDetail> {
  return api.delete<TenantDetail>(`/admin/tenants/${encodeURIComponent(id)}/logo`);
}

export function createBranch(tenantId: string, input: CreateBranchInput): Promise<BranchDto> {
  return api.post<BranchDto>(`/admin/tenants/${encodeURIComponent(tenantId)}/branches`, input);
}

export function createDevice(tenantId: string, input: CreateDeviceInput): Promise<DeviceWithToken> {
  return api.post<DeviceWithToken>(`/admin/tenants/${encodeURIComponent(tenantId)}/devices`, input);
}

export function rotateDeviceToken(deviceId: string): Promise<DeviceWithToken> {
  return api.post<DeviceWithToken>(`/admin/devices/${encodeURIComponent(deviceId)}/rotate-token`);
}

export function fetchDeviceDetail(
  deviceId: string,
  signal?: AbortSignal,
): Promise<DeviceDetail> {
  return api.get<DeviceDetail>(`/admin/devices/${encodeURIComponent(deviceId)}`, {
    signal,
  });
}

export function revokeDevice(deviceId: string): Promise<void> {
  return api.post<void>(`/admin/devices/${encodeURIComponent(deviceId)}/revoke`);
}

/**
 * Per-till reporting, within the tenant's entitlement. Switching this on for a
 * tenant that has not bought reporting changes nothing the till can see — the
 * server sends the AND of the two flags.
 */
export function setDeviceReporting(deviceId: string, enabled: boolean): Promise<DeviceDto> {
  return api.post<DeviceDto>(`/admin/devices/${encodeURIComponent(deviceId)}/reporting`, {
    enabled,
  });
}

/**
 * How often this till pushes, in minutes (5..180). The POS has no control of
 * its own for this — it mirrors whatever is set here and displays it read-only,
 * picking the change up on its next licence refresh.
 */
export function setDeviceSyncInterval(
  deviceId: string,
  intervalSec: number,
): Promise<DeviceDto> {
  return api.post<DeviceDto>(`/admin/devices/${encodeURIComponent(deviceId)}/sync-interval`, {
    intervalSec,
  });
}

/**
 * Adds a portal login to an existing tenant. Repeatable — call it as many
 * times as the tenant needs users, whenever it needs them; a tenant may also
 * have none at all if it only ever wanted a POS licence.
 */
export function addTenantUser(tenantId: string, input: CreateTenantUserRequest): Promise<TenantUserDto> {
  return api.post<TenantUserDto>(`/admin/tenants/${encodeURIComponent(tenantId)}/users`, input);
}

/**
 * Sets an existing portal user's password.
 *
 * The body is typed as ResetPasswordRequest so the field name is the
 * compiler's problem, not the user's. It sent `{ password }` before — the
 * name CreateTenantUserRequest uses — which the API rejects as an unknown
 * field, so the typed password never arrived and the only complaints shown
 * were about an absent `newPassword`.
 */
export function resetTenantUserPassword(tenantId: string, userId: string, password: string): Promise<void> {
  const body: ResetPasswordRequest = { newPassword: password };
  return api.post<void>(
    `/admin/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/reset-password`,
    body,
  );
}

/**
 * Clears a brute-force lockout without changing the password.
 *
 * The usual rescue: an owner who knows their password locked themselves out
 * and must wait 15 minutes. Resetting the password would work too, but it
 * changes a credential to fix a cooldown.
 */
export function unlockTenantUser(tenantId: string, userId: string): Promise<TenantUserDto> {
  return api.post<TenantUserDto>(
    `/admin/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/unlock`,
    {},
  );
}

/** A tenant's Ask Pilot balance, as the tenant sees it. */
export function fetchTenantAiCredits(tenantId: string, signal?: AbortSignal): Promise<AiCreditsDto> {
  return api.get<AiCreditsDto>(`/admin/tenants/${encodeURIComponent(tenantId)}/ai-credits`, { signal });
}

/** Boost credits: never expire, work on any plan, recorded in the audit log. */
export function grantAiCredits(tenantId: string, input: GrantAiCreditsRequest): Promise<void> {
  return api.post<void>(`/admin/tenants/${encodeURIComponent(tenantId)}/ai-credits`, input);
}

export function fetchSaSettings(deviceId: string, signal?: AbortSignal): Promise<DeviceSaSettings> {
  return api.get<DeviceSaSettings>(`/admin/devices/${encodeURIComponent(deviceId)}/sa-settings`, { signal });
}

export function updateSaSettings(deviceId: string, input: UpdateSaSettingsRequest): Promise<DeviceSaSettings> {
  return api.request<DeviceSaSettings>(`/admin/devices/${encodeURIComponent(deviceId)}/sa-settings`, {
    method: 'PUT',
    body: input,
  });
}

export function copySaSettings(deviceId: string, input: CopySaSettingsRequest): Promise<CopySaSettingsResponse> {
  return api.post<CopySaSettingsResponse>(
    `/admin/devices/${encodeURIComponent(deviceId)}/sa-settings/copy`,
    input,
  );
}
